import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useUnreadCount } from '../hooks/useNotifications.js';
import Logo from './Logo.jsx';
import { isCustomerApp } from '../lib/appTarget.js';

/* ---------------------------------------------------------------------------
 * Driver app chrome
 *
 * The driver build hides the storefront's header, footer and tab bar — it has
 * no shop to browse — which left the app as bare pages: no bar at the top, no
 * way between screens except a text "‹ Back", and no route at all to an
 * account screen. This is the shell that makes it an app: a title bar that
 * stays put, and thumb-level tabs for the three things a partner does.
 *
 * Icons are hand-written inline SVG. The app runs inside a Capacitor webview
 * with no network on the first paint, so an icon font or CDN is not an option.
 * ------------------------------------------------------------------------- */

const icon = (d, extra = null) => (
  <svg
    viewBox="0 0 24 24"
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={d} />
    {extra}
  </svg>
);

const ICONS = {
  route: icon('M4 6h11v9H4z M15 9h3.5L21 12v3h-6', <>
    <circle cx="8" cy="18" r="1.7" />
    <circle cx="17.5" cy="18" r="1.7" />
  </>),
  rupee: icon('M7 6h10M7 10h10M17 6c0 4-3 6-7 6l6 6'),
  bell: icon('M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9', <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />),
  person: icon('M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0'),
};

const TABS = [
  { to: '/driver', label: 'Today', icon: ICONS.route, end: true },
  { to: '/driver/wallet', label: 'Earnings', icon: ICONS.rupee },
  { to: '/driver/notifications', label: 'Alerts', icon: ICONS.bell, badge: true },
  { to: '/driver/account', label: 'Account', icon: ICONS.person },
];

/** Initials for the avatar button, falling back to a person glyph. */
function initials(name) {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

export default function DriverShell({ children }) {
  const { user } = useAuth();
  const unread = useUnreadCount();
  const { pathname } = useLocation();
  // In the customer build the driver area is a section of the shop, which has
  // its own header and tab bar — a second set would be one too many.
  if (isCustomerApp) return children;

  const who = initials(user?.fullName);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-oat/40">
      <header className="pad-safe-top sticky top-0 z-30 flex items-center gap-2 border-b border-oat-dark/60 bg-surface/90 px-3 backdrop-blur-md">
        <Link to="/driver" className="flex min-h-touch items-center" aria-label="Today’s deliveries">
          <Logo />
        </Link>
        <Link
          to="/driver/account"
          aria-label="Your account"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-leaf-100 font-display text-sm font-semibold text-brand transition-colors active:bg-leaf-300"
        >
          {who ?? ICONS.person}
        </Link>
      </header>

      {/* Bottom padding clears the fixed tab bar, so the last card of every
          screen is reachable rather than parked under it. */}
      <main className="min-w-0 flex-1 pb-24">{children}</main>

      <nav
        aria-label="Sections"
        className="pad-safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-oat-dark/70 bg-surface/95 shadow-[0_-6px_20px_-8px_rgba(14,59,34,0.14)] backdrop-blur-md"
      >
        <ul className="mx-auto flex max-w-2xl items-stretch justify-around px-1 pt-1">
          {TABS.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  [
                    'flex min-h-touch flex-col items-center justify-center gap-1 rounded-2xl py-1.5 text-2xs font-semibold transition-colors duration-150',
                    isActive || (tab.end && pathname === tab.to)
                      ? 'text-heading'
                      : 'text-ink-faint active:text-brand',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        'relative flex items-center justify-center rounded-full px-4 py-0.5 transition-colors duration-150',
                        isActive ? 'bg-leaf-100' : 'bg-transparent',
                      ].join(' ')}
                    >
                      {tab.icon}
                      {/* Unread alerts are the one thing a driver must not miss,
                          so the count rides the tab itself. */}
                      {tab.badge && unread > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-persimmon px-1 text-[10px] font-bold tabular-nums text-white">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </span>
                    {tab.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
